import React from 'react';
import { Card, Table, Icon, InfoText, Numbers, TooltipHelp } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../context/useAppContext';
import Style from './paymentPlanStats.module.css';
import i18n from 'i18next';
import LoadingTable from '../../components/loadingTable';

function HeaderRow() {
    const { t } = useTranslation();

    return (
        <Table.Tr isHeader>
            <th className={Style['width-x-large']}>
                {t('paymentinvoicing.payment_types.payment_plan')}
                <TooltipHelp triggerOnHover title={t('ticket.attendees_stats.payment_plan.columns.payment_plan_tooltip')} />
            </th>
            <th className={`${Style['width-regular']} ${Style['min-width']}`}>
                {t('ticket.attendees_stats.payment_plan.columns.aborted')}
                <TooltipHelp triggerOnHover title={t('ticket.attendees_stats.payment_plan.columns.aborted_tooltip')} />
            </th>
            <th className={Style['width-regular']}>{t('ticket.attendees_stats.payment_plan.columns.in_progress')}</th>
            <th className={Style['width-regular']}>{t('ticket.attendees_stats.payment_plan.columns.completed')}</th>
            <th className={Style['width-regular']}>{t('ticket.attendees_stats.payment_plan.columns.total')}</th>
            <th className={Style['width-mini']}></th>
        </Table.Tr>
    );
}

export const GlobalStatRow = React.memo(({ data }) => {
    const [{ organization }] = useAppContext();
    const locale = i18n.language;
    const { t } = useTranslation();

    return (
        <Table.Tr className={Style['global-stat-row']}>
            <Table.Td className={Style['bold']}>{t('ticket.attendees_stats.payment_plan.columns.total')}</Table.Td>
            <Table.Td className={Style['stats-infotext-w-label']}>
                <InfoText subtitle={<Numbers value={data.amount_paid_terminated} locale={locale} currency={organization?.currency || 'EUR'} showNullDecimal />}>
                    <Numbers value={+data.count_terminated} locale={locale} showNullDecimal />
                    {t('ticket.common.order', { count: +data.count_terminated })}
                </InfoText>
            </Table.Td>
            <Table.Td className={Style['stats-infotext-w-label']}>
                <InfoText subtitle={<Numbers value={data.amount_paid_pending} locale={locale} currency={organization?.currency || 'EUR'} showNullDecimal />}>
                    <Numbers value={+data.count_pending} locale={locale} showNullDecimal />
                    {t('ticket.common.order', { count: +data.count_pending })}
                </InfoText>
            </Table.Td>
            <Table.Td className={Style['stats-infotext-w-label']}>
                <InfoText subtitle={<Numbers value={data.amount_paid_validated} locale={locale} currency={organization?.currency || 'EUR'} showNullDecimal />}>
                    <Numbers value={+data.count_validated} locale={locale} showNullDecimal />
                    {t('ticket.common.order', { count: +data.count_validated })}
                </InfoText>
            </Table.Td>
            <Table.Td className={Style['stats-infotext-w-label']}>
                <InfoText subtitle={<Numbers value={data.amount_paid_total} locale={locale} currency={organization?.currency || 'EUR'} showNullDecimal />}>
                    <Numbers value={+data.count_total} locale={locale} showNullDecimal />
                    {t('ticket.common.order', { count: +data.count_total })}
                </InfoText>
            </Table.Td>
            <td></td>
        </Table.Tr>
    );
});

export const PaymentPlanRow = React.memo(({ data }) => {
    const [{ organization }] = useAppContext();
    const locale = i18n.language;
    const { t } = useTranslation();

    const [isExpanded, setIsExpanded] = React.useState(true);

    const handleClick = () => {
        setIsExpanded(currentState => !currentState);
    };

    const getPercentage = React.useCallback(
        stat => {
            let _percentage = stat >= 0 ? (stat * 100) / data.count_total : 0;
            return _percentage.toFixed(2);
        },
        [data]
    );

    return (
        <>
            <Table.Tr className={Style['product-row']} onClick={handleClick}>
                <Table.Td>
                    <InfoText subtitle={t('ticket.attendees_stats.payment_plan.instalment_label', { count: data.number_instalments })}>
                        <span className={Style['bold']}>{t('paymentinvoicing.payment_types.payment_plan')}</span>
                    </InfoText>
                </Table.Td>
                <Table.Td className={Style['stats-w-label']}>
                    <span>
                        <Numbers value={+data.count_terminated} locale={locale} showNullDecimal />
                        {t('ticket.common.order', { count: +data.count_terminated })}
                    </span>
                </Table.Td>
                <Table.Td className={Style['stats-w-label']}>
                    <span>
                        <Numbers value={+data.count_pending} locale={locale} showNullDecimal />
                        {t('ticket.common.order', { count: +data.count_pending })}
                    </span>
                </Table.Td>
                <Table.Td className={Style['stats-w-label']}>
                    <span>
                        <Numbers value={+data.count_validated} locale={locale} showNullDecimal />
                        {t('ticket.common.order', { count: +data.count_validated })}
                    </span>
                </Table.Td>
                <Table.Td className={Style['stats-w-label']}>
                    <span>
                        <Numbers value={+data.count_total} locale={locale} showNullDecimal />
                        {t('ticket.common.order', { count: +data.count_total })}
                    </span>
                </Table.Td>
                <td>
                    <Icon name={'chevron-bottom'} className={isExpanded ? Style['rotate-180'] : ''} onClick={handleClick} />
                </td>
            </Table.Tr>
            {isExpanded && (
                <>
                    <Table.Tr>
                        <Table.Td>{t('ticket.common.percentage')}</Table.Td>
                        <Table.Td>{`${getPercentage(data.count_terminated)}%`}</Table.Td>
                        <Table.Td>{`${getPercentage(data.count_pending)}%`}</Table.Td>
                        <Table.Td>{`${getPercentage(data.count_validated)}%`}</Table.Td>
                        <Table.Td>{`100 %`}</Table.Td>
                        <td></td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Td>{t('ticket.attendees_stats.payment_plan.rows.amount_paid')}</Table.Td>
                        <Table.Td>
                            <Numbers value={data.amount_paid_terminated} locale={locale} currency={organization?.currency || 'EUR'} showNullDecimal />
                        </Table.Td>
                        <Table.Td>
                            <Numbers value={data.amount_paid_pending} locale={locale} currency={organization?.currency || 'EUR'} showNullDecimal />
                        </Table.Td>
                        <Table.Td>
                            <Numbers value={data.amount_paid_validated} locale={locale} currency={organization?.currency || 'EUR'} showNullDecimal />
                        </Table.Td>
                        <Table.Td>
                            <Numbers value={data.amount_paid_total} locale={locale} currency={organization?.currency || 'EUR'} showNullDecimal />
                        </Table.Td>
                        <td></td>
                    </Table.Tr>
                    <Table.Tr>
                        <Table.Td>{t('ticket.attendees_stats.payment_plan.rows.remaining_amount')}</Table.Td>
                        <Table.Td>-</Table.Td>
                        <Table.Td>
                            <Numbers value={data.amount_remaining_paid_pending} locale={locale} currency={organization?.currency || 'EUR'} showNullDecimal />
                        </Table.Td>
                        <Table.Td>-</Table.Td>
                        <Table.Td>
                            <Numbers value={data.amount_remaining_paid_total} locale={locale} currency={organization?.currency || 'EUR'} showNullDecimal />
                        </Table.Td>
                        <td></td>
                    </Table.Tr>
                </>
            )}
        </>
    );
});

export default function PaymentPlanStats({ stats = null }) {
    const { t } = useTranslation();

    const paymentPlanStats = React.useMemo(() => {
        if (!stats) {
            return null;
        }

        return stats.find(groupStats => groupStats.name === 'payment_plan_stats')?.value;
    }, [stats]);

    const paymentPlanStatsExist = React.useMemo(() => {
        if (!paymentPlanStats) {
            return false;
        }

        return Boolean(paymentPlanStats?.['stats_by_type']?.length);
    }, [paymentPlanStats]);

    return (
        <Card separator="false" className={Style['payment-plan-stats__container']}>
            <Card.Content>
                <Table useNew>
                    <Table.THead>
                        <HeaderRow />
                    </Table.THead>
                    {paymentPlanStatsExist && (
                        <Table.TBody>
                            <GlobalStatRow data={paymentPlanStats['global_stats']} />
                            {paymentPlanStats['stats_by_type'].map((s, index) => (
                                <PaymentPlanRow key={index} data={s} />
                            ))}
                        </Table.TBody>
                    )}
                </Table>
                <LoadingTable loading={!stats} />
                {stats && !paymentPlanStatsExist && <Card.EmptyState title={t('ticket.attendees_stats.channels.noChannelFound')} icon={<Icon name="cart" />} />}
            </Card.Content>
        </Card>
    );
}
